<script setup lang="ts">
import type { UserLimit, UserLimitPayload } from '@/types/UserLimits';

type Props = {
  hideSubmit?: boolean;
  hideLabel?: boolean;
};

withDefaults(defineProps<Props>(), {
  hideLabel: false,
});

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { getDateTime } = useDateTimeHelper();
const { showMoneyFormat } = useWallet();
const { openToaster } = useLayoutStore();

const { userCurrency, userCountry, userLanguage } = useUserData();
const { userLimits, setAccountLimits } = useUserLimits();

const { choiceOptions, periodsOptions, getTimeToNextChange, getPeriodLabel } =
  useAccountProtection();

const isLoading = ref(false);

const userLimitLoss = ref<UserLimit>({
  active: 0,
  period: '0',
  amount: 0,
});

const formattedScheduledAmount = computed(() => {
  return Number(userLimits.value?.user_limit_loss_next_amount_change) > 0
    ? moneyFormatter(userLimits.value?.user_limit_loss_next_amount_change)
    : t('general.unlimited');
});

const moneyFormatter = (value?: string | number) => {
  if (!value) {
    return;
  }

  return showMoneyFormat({
    value: Number(value) / 100,
  });
};

async function handleSubmit() {
  if (isLoading.value) {
    return;
  }

  if (userLimitLoss.value.active) {
    let message;

    if (!Number(userLimitLoss.value.period)) {
      message = t('account.account_protection.select_period');
    } else if (!Number(userLimitLoss.value.amount)) {
      message = t('account.account_protection.type_amount');
    }

    if (message) {
      openToaster({
        message,
        type: 'error',
      });

      return;
    }
  }

  await $containment.handle('update_limits', () => {
    sendSubmit();
  });
}

async function sendSubmit() {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isLoading.value = true;

  const payload: UserLimitPayload = {
    type: 'loss',
  };

  if (Number(userLimitLoss.value.active)) {
    payload.user_limit_loss_active = Number(userLimitLoss.value.active);
    payload.user_limit_loss_period = Number(userLimitLoss.value.period);
    payload.user_limit_loss = Number(userLimitLoss.value.amount);
  } else {
    payload.user_limit_loss_active = 0;
  }

  if ($kyc?.current?.id) {
    payload.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    payload.password_check = temporaryPassword.get();
  }

  await setAccountLimits(payload)
    .then((res: any) => {
      const message =
        res.data?.message || t('account.account_protection.update_success');

      openToaster({
        message,
        type: 'success',
      });

      userLimitLoss.value.period = String(
        userLimits.value?.user_limit_loss_period,
      );
      userLimitLoss.value.amount = userLimits.value?.user_limit_loss ?? 0;
    })
    .catch((error: any) => {
      const message =
        error.data?.message ||
        error.message ||
        t('account.account_protection.generic_error');

      openToaster({
        message,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
      temporaryPassword.clear();
    });
}

const setUserLimits = () => {
  if (userLimits.value?.spa_limit_loss) {
    userLimitLoss.value.active = 1;
    userLimitLoss.value.period = '4';
    userLimitLoss.value.amount = Number(userLimits.value.spa_limit_loss);
  } else {
    if (userLimits.value?.user_limit_loss_active) {
      userLimitLoss.value.active = Number(
        userLimits.value?.user_limit_loss_active,
      );
    }

    if (userLimits.value?.user_limit_loss_period) {
      userLimitLoss.value.period = String(
        userLimits.value?.user_limit_loss_period,
      );
    }

    if (userLimits.value?.user_limit_loss) {
      userLimitLoss.value.amount = Number(userLimits.value?.user_limit_loss);
    }
  }
};

defineExpose({
  userLimitLoss,
});

onMounted(() => {
  setUserLimits();
});
</script>

<template>
  <div :class="$style.lostLimit">
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div :class="$style.groupRadio">
      <label v-if="!hideLabel">
        {{ $t('account.account_protection.set_limit') }}
      </label>

      <BaseFormRadio
        v-model="userLimitLoss.active"
        :options="choiceOptions"
      />
    </div>

    <div
      v-if="!!userLimitLoss.active"
      :class="$style.groupFields"
    >
      <div :class="$style.groupInput">
        <BaseFormSelectField
          id="user_limit_loss_period"
          v-model="userLimitLoss.period"
          :initial-value="userLimitLoss.period"
          name="user_limit_loss_period"
          :options="periodsOptions"
        />
      </div>

      <div :class="$style.groupInput">
        <BaseFormInputCurrency
          id="userLimitLoss"
          v-model="userLimitLoss.amount"
          name="value"
          required
          :show-currency="true"
          size="lg"
          :user-country="userCountry"
          :user-currency="userCurrency"
          :user-language="userLanguage"
        />
      </div>
    </div>

    <div
      v-if="
        userLimits?.user_limit_loss_next_amount_change &&
        userLimits?.user_limit_loss_next_amount_change_requested_at
      "
      :class="$style.lastChange"
    >
      <p>
        <strong>{{ $t('general.active') }}:</strong>
        {{ moneyFormatter(userLimits?.user_limit_loss) }} -
        {{ getDateTime(userLimits?.user_limit_loss_change) }}
      </p>

      <p
        v-html="
          $t('account.account_protection.user_limit_scheduled_change', {
            amount: formattedScheduledAmount,
            period: getPeriodLabel(
              userLimits?.user_limit_loss_next_period_change,
            ),
            requestedAt: getDateTime(
              userLimits?.user_limit_loss_next_amount_change_requested_at,
            ),
            activeAt: getTimeToNextChange(userLimits?.user_limit_loss_change),
          })
        "
      />
    </div>

    <button
      v-if="!hideSubmit"
      :class="$style.btnSubmit"
      @click="handleSubmit()"
    >
      <NuxtIcon name="check" />
      {{ $t('account.save') }}
    </button>

    <div
      v-if="!!moneyFormatter(userLimits?.spa_limit_loss)"
      class="alert alert-warning"
      :class="$style.alertSpa"
    >
      <p
        v-html="
          $t('account.account_protection.spa_limit_alert', {
            limit: t('account.account_protection.user_limit_loss'),
            value: moneyFormatter(userLimits?.spa_limit_loss),
          })
        "
      ></p>
    </div>
  </div>
</template>

<style module lang="postcss">
.lostLimit {
  @apply w-full;

  .groupRadio {
    @apply flex items-center gap-5 font-semibold text-xs md:text-sm text-texts;
  }

  .groupFields {
    @apply flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-5 mt-5;

    .groupInput {
      @apply w-full min-w-40 md:max-w-52;
    }
  }

  .lastChange {
    @apply text-2xs text-warning/70 mt-4;
  }

  .alertSpa {
    @apply mt-4 text-xs;

    strong {
      &:first-child {
        @apply lowercase;
      }
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 mt-5 disabled:opacity-40 disabled:cursor-not-allowed;
  }
}
</style>
